// src/utils/funnel.ts
// 登録 → 初回学習 → 継続 → 課金 のファネルを「後から必ず追える」形で二重記録する。
//
// 1) GA4 へイベント送信（外部の分析用）
// 2) Firestore users/{uid}/funnel/{step} へ節目を書く（社内から読める冗長化。
//    GA4 Data API の権限が無い状態でも、実データが手元に残る）
//
// 指令: .company/hq 2026-09-23（CEO「その後の動きを分析。取れていなければ環境を強化」）
import { doc, getDoc, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '@/firebase';
import { SUBJECT_BY_CAMPAIGN } from '@/generated/noteLanding';
import { trackGA4 } from './analytics';

export type FunnelStep =
  | 'sign_up'                  // 新規登録
  | 'first_question_answered'  // 初回の解答（= 実際に学習を始めた）
  | 'question_answered'        // 2回目以降の解答
  | 'question_answered_first_unknown' // 解答したが「初回かどうか」を確認できなかった（通信失敗等）
  | 'session_resumed'          // 別セッションでの再訪（= 継続）
  | 'paywall_view'             // 課金導線の表示
  | 'checkout_start'           // 決済ページへ遷移
  | 'purchase';                // 課金完了（サーバー側でも書く）

type Params = Record<string, string | number | boolean | undefined>;

/**
 * Firestore は undefined を書けない（クライアントSDKは例外を投げる）。
 * utm が付いていない通常の流入では未定義項目が出るので、書き込む前に落とす。
 */
function stripUndefined(params: Params): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

/**
 * ファネルの節目を1件記録する。
 * 解析用であり、本処理を止めてはならないので失敗は握り潰す。
 */
export async function recordFunnel(
  step: FunnelStep,
  uid: string | null | undefined,
  params: Params = {}
): Promise<boolean> {
  // **すべての節目に流入元（初回接触）を添える。**
  // 以前は sign_up にだけ付けていたので、「どの記事から来た人が**1問解いたか**」が
  // 集計できなかった（週次レポートの記事別の表は utm_source を見て振り分けるため、
  // first_question_answered に utm が無いと**全部落ちていた**。2026-09-29 に気づいた）。
  // params 側を後ろに置いているので、呼び出し側が明示した値が優先される。
  const touch = getFirstTouch();
  // 静的ページのどのCTAから来たか（途中CTA / 採点後CTA）。
  // **ファーストタッチとは別の鍵**に置いてある（流入元は上書きしない）。
  // これが無いと、note の記事key は同じままなので「どちらのCTAが効いたか」が分からない。
  let lastCta: string | undefined;
  try {
    lastCta = localStorage.getItem('last_cta') || undefined;
  } catch (_) {
    // localStorage が使えない環境では付けない
  }
  params = { ...touch, ...(lastCta ? { last_cta: lastCta } : {}), ...params } as Params;

  // 1) GA4（本番ホストのみ送信される）
  try {
    trackGA4(step, params);
  } catch (e) {
    console.warn('[funnel] GA4 send failed', e);
  }

  // 2) Firestore（ログイン中のみ。未ログインの導線は GA4 側で追う）
  // 戻り値は「Firestore に書けたか」。呼び出し側が後片付けの可否を判断できるようにする。
  if (!uid) return false;
  try {
    const clean = stripUndefined(params);
    const ref = doc(db, 'users', uid, 'funnel', step);
    const snap = await getDoc(ref);
    await setDoc(
      ref,
      {
        step,
        count: increment(1),
        lastAt: serverTimestamp(),
        lastParams: clean,
        ...(snap.exists() ? {} : { firstAt: serverTimestamp(), firstParams: clean }),
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.warn('[funnel] Firestore write failed', e);
    return false;
  }
}

/**
 * 画面遷移の直前に使う版。
 *
 * `void recordFunnel(...)` で撃ちっぱなしにすると、Firestore への書き込みが飛ぶ前に
 * `window.location` が変わり、**記録ごと失われる**。実際 2026-09-24 09:40 の定期チェックで
 * checkout_start（＝課金を決断したという最重要の段）が欠測した。
 *
 * とはいえ決済のクリックを待たせ続けるのは本末転倒なので、上限時間で打ち切る。
 * 「記録のために課金を止める」ことはしない。
 */
export async function recordFunnelBeforeNavigation(
  step: FunnelStep,
  uid: string | null | undefined,
  params: Params = {},
  timeoutMs = 2500
): Promise<void> {
  await Promise.race([
    recordFunnel(step, uid, params),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

/**
 * 解答を1件記録する。**その人の最初の1問なら first_question_answered、以降は question_answered。**
 *
 * 2026-09-26: これまで初回学習の記録は statsUtils.recordAnswer() の中にあったが、
 * **その関数はどこからも呼ばれていない死んだコード**だった。つまり
 * 「登録した人が初回学習まで進んだか」という 10/26 の判定指標は、
 * ユーザーが何問解いても**発火する余地が無かった**。実際に解答が確定する画面から直接呼ぶ。
 */
export async function recordAnswered(
  uid: string | null | undefined,
  params: Params = {}
): Promise<boolean> {
  if (!uid) return false;

  // 既存の記録が読めたかどうかで3通りに分ける。
  // **「確認できなかった」を「初回ではない」に丸めない。**
  // 丸めると、通信の一時失敗で本当の初回解答が2問目以降として記録され、
  // 「初回学習0件」に見えてしまう（= 10/26 の判定を直接ゆがめる）。
  // どちらに倒しても嘘になるので、第3の値として別イベントで残す。
  // 【2026-09-26 修正】当初は try/catch だけだったが、**Firestore の getDoc は
  // ネットワーク断では reject せず待ち続ける**（実測: 該当ドキュメントの読み取りだけを
  // 遮断したところ、例外にならず記録が丸ごと欠測した）。
  // 例外だけを見ていると「通信が死んだときに何も残らない」ので、**時間切れも unknown に倒す**。
  const READ_TIMEOUT_MS = 3000;
  let state: 'first' | 'repeat' | 'unknown' = 'unknown';
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const snap = await Promise.race([
        getDoc(doc(db, 'users', uid, 'funnel', 'first_question_answered')),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), READ_TIMEOUT_MS)),
      ]);
      if (snap === null) {
        // 時間切れ。1回だけ短く待って再試行し、それでも駄目なら unknown のまま進む
        if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
        continue;
      }
      state = snap.exists() ? 'repeat' : 'first';
      break;
    } catch (_) {
      if (attempt === 0) await new Promise((r) => setTimeout(r, 400)); // 短い再試行
    }
  }

  const step: FunnelStep =
    state === 'first'
      ? 'first_question_answered'
      : state === 'repeat'
      ? 'question_answered'
      : 'question_answered_first_unknown';
  return recordFunnel(step, uid, params);
}

/**
 * 静的ページと同じ「節目」をアプリ側からも送る。
 *
 * 静的ページ(/learn/** /mock/**)は `__chuStep` で直接 /api/trackFunnel を叩いている。
 * 同じ導線の続き（コミュニティに着いた・投稿した）はアプリ側で起きるので、
 * **同じ受け口に同じ形で送る**。名前はサーバ側の固定許可リストにあるものだけが記録される。
 *
 * 失敗しても本処理は止めない（計測のために画面を止めない）。
 */
export function recordStep(step: string): void {
  try {
    const t = getFirstTouch();
    const s = t.utm_source || 'direct';
    const c = t.utm_campaign || 'none';
    const url =
      'https://us-central1-chusho-app.cloudfunctions.net/api/trackFunnel' +
      `?step=${encodeURIComponent(step)}&utm_source=${encodeURIComponent(s)}&utm_campaign=${encodeURIComponent(c)}`;
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
    void fetch(url, { method: 'POST', keepalive: true }).catch(() => undefined);
  } catch (_) {
    // 計測は落ちてよい
  }
}

const SESSION_KEY = 'funnel_session_marked';

/**
 * 1ブラウザセッションにつき1回だけ記録する（再訪の計測用）。
 */
export async function recordFunnelOncePerSession(
  step: FunnelStep,
  uid: string | null | undefined,
  params: Params = {}
): Promise<void> {
  try {
    const marked = sessionStorage.getItem(`${SESSION_KEY}:${step}`);
    if (marked) return;
    sessionStorage.setItem(`${SESSION_KEY}:${step}`, '1');
  } catch (_) {
    // sessionStorage が使えない環境では毎回記録する（欠測より重複のほうがまし）
  }
  await recordFunnel(step, uid, params);
}

/**
 * 静的ミニ模試（/mock/**）で解いた分を拾って記録する。
 *
 * 静的ページは SPA の外で Firebase 認証が無いため、その場では Firestore に書けない。
 * 代わりに localStorage に印を置いてもらい、ログイン済みのアプリ側で回収する。
 * （2026-09-26 司令塔指摘「/mock/ で解いた人が数えられない可能性」への対処）
 *
 * 登録前に解いた分も「この製品で学習した」事実なので数えるが、
 * surface='mock_static' が残るので後から切り分けられる。
 */
export async function flushPendingAnswers(uid: string | null | undefined): Promise<void> {
  if (!uid) return;
  let pending: { count?: number; firstAt?: string; subject?: string; lastScore?: number; surface?: string } | null = null;
  try {
    const raw = localStorage.getItem(PENDING_ANSWERS_KEY);
    if (!raw) return;
    pending = JSON.parse(raw);
  } catch (_) {
    return;
  }
  if (!pending || !pending.count) return;
  try {
    const written = await recordAnswered(uid, {
      surface: pending.surface || 'mock_static',
      answered_count: pending.count,
      subject: pending.subject,
      score: pending.lastScore,
      answered_before_signup: true,
    });
    // **書けたことを確認してから印を消す。**
    // 以前は結果を見ずに消していたため、通信が不安定なときに
    // 「印は消えたが記録は残っていない」状態になり、模試で解いた事実が失われていた
    // （2026-09-26 の週1検査で実際に再現。印を置いた目的そのものが壊れる）。
    if (written) localStorage.removeItem(PENDING_ANSWERS_KEY);
    else console.warn('[funnel] pending answers kept (write failed)');
  } catch (e) {
    console.warn('[funnel] pending answers flush failed', e);
  }
}

const PENDING_ANSWERS_KEY = 'pending_answers';

/* ------------------------------------------------------------------ */
/* 初回接触（流入元）の記録                                            */
/* ------------------------------------------------------------------ */
// GA4 の権限が無く、検索エンジンの索引数も外部から信頼できる形では測れない。
// ならば「来た人がどこから来たか」は製品側で持つ。
// 最初に開いたときの utm / リファラ / 着地パスを1度だけ保存し、登録時にファネルへ添える。
const FIRST_TOUCH_KEY = 'first_touch';

export interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_host?: string;
  landing_path?: string;
  landed_at?: string;
}

/** アプリ起動時に1度だけ呼ぶ。既に記録済みなら上書きしない（ファーストタッチを保持）。 */
export function captureFirstTouch(): void {
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const q = new URLSearchParams(window.location.search);
    let referrerHost = '';
    try {
      if (document.referrer) {
        const r = new URL(document.referrer);
        // 自サイト内の遷移はリファラとして意味が無いので落とす
        if (r.host !== window.location.host) referrerHost = r.host;
      }
    } catch (_) { /* noop */ }

    const touch: FirstTouch = {
      utm_source: q.get('utm_source') || undefined,
      utm_medium: q.get('utm_medium') || undefined,
      utm_campaign: q.get('utm_campaign') || undefined,
      referrer_host: referrerHost || undefined,
      landing_path: window.location.pathname,
      landed_at: new Date().toISOString(),
    };
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  } catch (_) {
    // localStorage が使えない環境では流入元を諦める（本処理には影響させない）
  }
}

/** 記録済みの初回接触。無ければ空。 */
export function getFirstTouch(): FirstTouch {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? (JSON.parse(raw) as FirstTouch) : {};
  } catch (_) {
    return {};
  }
}

/* ------------------------------------------------------------------ */
/* 初回接触から「最初の1問」の科目を決める                              */
/* ------------------------------------------------------------------ */
// note の記事ごとに着地先が違う（導線仕様 .company/ventures/note-stats/chusho-landing-spec.md）。
// 読んできた題材と最初の1問を揃えると、記事の続きとして自然につながる。
// 反応が最も良い記事が財務・会計（スキ率52.4%）なので、判別できない場合もそこに寄せる。
const SUBJECT_BY_PATH: Array<[RegExp, string]> = [
  [/\/learn\/finance/, 'finance'],
  [/\/learn\/economics/, 'economics'],
  [/\/learn\/management/, 'management'],
  [/\/learn\/operations/, 'operations'],
  [/\/learn\/law/, 'law'],
  [/\/learn\/information/, 'information'],
  [/\/learn\/policy/, 'policy'],
];

// note の記事key → 科目。**手で書かない。** 正本は scripts/seo/note-landings.json で、
// 短縮リンク(/go/note/<記事key>)の着地先と同じ表から生成する
// （2か所に書くと必ずズレる。論点数の表記で実際にズレた）。

/** 最初の1問に使う科目。判別できなければ finance。 */
export function firstQuestionSubject(): string {
  const t = getFirstTouch();
  const c = t.utm_campaign && SUBJECT_BY_CAMPAIGN[t.utm_campaign];
  if (c) return c;
  const path = t.landing_path || '';
  for (const [re, subject] of SUBJECT_BY_PATH) if (re.test(path)) return subject;
  return 'finance';
}

/** 問題が揃っている最新年度（sme は問題が無いので候補から除く） */
export const FIRST_QUESTION_YEAR = '2025';
